import { useLayoutEffect } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

function useWorkPageAnimations(pageRef, activeCategory) {
  useLayoutEffect(() => {
    if (!pageRef.current) {
      return;
    }

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReducedMotion) {
      return;
    }

    const ctx = gsap.context(() => {
      const heroEyebrow =
        pageRef.current.querySelector(".work-hero-eyebrow");

      const heroTitleLines =
        pageRef.current.querySelectorAll(".work-hero-title > span");

      const heroParagraph =
        pageRef.current.querySelector(".work-hero-bottom > p");

      const projectCount =
        pageRef.current.querySelector(".work-project-count");

      const outlineText =
        pageRef.current.querySelector(".work-outline-text");

      const workHero =
        pageRef.current.querySelector(".work-hero");

      const filter =
        pageRef.current.querySelector(".work-filter");

      const archiveFooterItems =
        pageRef.current.querySelectorAll(
          ".work-archive-footer > *"
        );

      const heroTimeline = gsap.timeline({
        defaults: {
          ease: "power3.out",
        },
      });

      if (heroEyebrow) {
        heroTimeline.from(heroEyebrow, {
          opacity: 0,
          y: 20,
          duration: 0.7,
        });
      }

      if (heroTitleLines.length) {
        heroTimeline.from(
          heroTitleLines,
          {
            opacity: 0,
            yPercent: 110,
            duration: 1.1,
            stagger: 0.14,
          },
          "-=0.35"
        );
      }

      if (heroParagraph) {
        heroTimeline.from(
          heroParagraph,
          {
            opacity: 0,
            y: 30,
            duration: 0.75,
          },
          "-=0.5"
        );
      }

      if (projectCount) {
        heroTimeline.from(
          projectCount,
          {
            opacity: 0,
            y: 25,
            duration: 0.7,
          },
          "-=0.55"
        );
      }

      /*
      ========================================================
      MOVES PEOPLE
      0% -> 100% NEON BLUE

      No scrub.

      Scroll down past 70px:
      immediately animate to full neon blue.

      Scroll back above 70px:
      immediately animate back to outline.
      ========================================================
      */

      if (outlineText && workHero) {
        gsap.set(outlineText, {
          color: "transparent",
          webkitTextStrokeColor:
            "rgba(255, 255, 255, 0.68)",
        });

        const fillOutlineText = () => {
          gsap.to(outlineText, {
            color: "var(--work-blue)",
            webkitTextStrokeColor: "var(--work-blue)",
            duration: 0.38,
            ease: "power2.out",
            overwrite: "auto",
          });
        };

        const resetOutlineText = () => {
          gsap.to(outlineText, {
            color: "transparent",
            webkitTextStrokeColor:
              "rgba(255, 255, 255, 0.68)",
            duration: 0.3,
            ease: "power2.out",
            overwrite: "auto",
          });
        };

        ScrollTrigger.create({
          trigger: workHero,

          start: "top+=70 top",

          onEnter: fillOutlineText,

          onLeaveBack: resetOutlineText,

          invalidateOnRefresh: true,
        });
      }

      /*
      ========================================================
      PROJECT COUNT
      ========================================================
      */

      const countElement =
        pageRef.current.querySelector(
          ".work-project-count-value"
        );

      if (countElement) {
        const finalValue = Number(
          countElement.dataset.value
        );

        const counter = {
          value: 0,
        };

        gsap.to(counter, {
          value: finalValue,
          duration: 1.6,
          delay: 0.45,
          ease: "power2.out",

          onUpdate: () => {
            countElement.textContent = String(
              Math.round(counter.value)
            ).padStart(2, "0");
          },
        });
      }

      /*
      ========================================================
      FILTER REVEAL
      ========================================================
      */

      if (filter) {
        gsap.from(filter, {
          opacity: 0,
          y: 30,
          duration: 0.75,
          ease: "power3.out",

          scrollTrigger: {
            trigger: filter,
            start: "top 88%",
            once: true,
          },
        });
      }

      /*
      ========================================================
      ARCHIVE FOOTER
      ========================================================
      */

      if (archiveFooterItems.length) {
        gsap.from(archiveFooterItems, {
          opacity: 0,
          y: 25,
          stagger: 0.1,
          duration: 0.7,
          ease: "power3.out",

          scrollTrigger: {
            trigger: ".work-archive-footer",
            start: "top 90%",
            once: true,
          },
        });
      }
    }, pageRef);

    const refreshTimer = window.setTimeout(() => {
      ScrollTrigger.refresh();
    }, 400);

    return () => {
      window.clearTimeout(refreshTimer);

      ctx.revert();
    };
  }, [pageRef]);

  /*
  ============================================================
  PROJECT ANIMATIONS

  Runs again whenever the category changes.
  ============================================================
  */

  useLayoutEffect(() => {
    if (!pageRef.current) {
      return;
    }

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReducedMotion) {
      return;
    }

    const projectContext = gsap.context(() => {
      const projectCards = gsap.utils.toArray(
        ".work-project"
      );

      projectCards.forEach((card, index) => {
        const imageFrame = card.querySelector(
          ".work-project-image"
        );

        const image = card.querySelector(
          ".work-project-image img"
        );

        const information = card.querySelector(
          ".work-project-information"
        );

        /*
        ======================================================
        PROJECT CARD REVEAL
        ======================================================
        */

        gsap.from(card, {
          opacity: 0,
          y: 60,
          duration: 0.85,
          delay: (index % 3) * 0.08,
          ease: "power3.out",

          scrollTrigger: {
            trigger: card,
            start: "top 88%",
            once: true,
          },
        });

        /*
        ======================================================
        IMAGE CLIP REVEAL
        ======================================================
        */

        if (imageFrame) {
          gsap.from(imageFrame, {
            clipPath: "inset(0 0 100% 0)",
            duration: 1.15,
            ease: "power4.inOut",

            scrollTrigger: {
              trigger: card,
              start: "top 88%",
              once: true,
            },
          });
        }

        /*
        ======================================================
        IMAGE PARALLAX
        ======================================================
        */

        if (image) {
          gsap.fromTo(
            image,
            {
              yPercent: -5,
            },
            {
              yPercent: 5,
              ease: "none",

              scrollTrigger: {
                trigger: card,
                start: "top bottom",
                end: "bottom top",
                scrub: true,
                invalidateOnRefresh: true,
              },
            }
          );
        }

        /*
        ======================================================
        PROJECT INFORMATION REVEAL
        ======================================================
        */

        if (information) {
          gsap.from(information.children, {
            opacity: 0,
            y: 20,
            stagger: 0.07,
            duration: 0.6,
            ease: "power3.out",

            scrollTrigger: {
              trigger: information,
              start: "top 94%",
              once: true,
            },
          });
        }
      });

      /*
      ========================================================
      VIEW PROJECT FOLLOW INTERACTION
      ========================================================
      */

      const media = gsap.matchMedia();

      media.add("(min-width: 701px)", () => {
        const cleanupFunctions = [];

        projectCards.forEach((card) => {
          const imageFrame = card.querySelector(
            ".work-project-image"
          );

          const viewProject = card.querySelector(
            ".work-project-view"
          );

          const image = card.querySelector(
            ".work-project-image img"
          );

          if (!imageFrame || !viewProject) {
            return;
          }

          gsap.set(viewProject, {
            xPercent: -50,
            yPercent: -50,
            x: 0,
            y: 0,
            scale: 0.88,
            opacity: 0,
          });

          const moveX = gsap.quickTo(
            viewProject,
            "x",
            {
              duration: 0.45,
              ease: "power3.out",
            }
          );

          const moveY = gsap.quickTo(
            viewProject,
            "y",
            {
              duration: 0.45,
              ease: "power3.out",
            }
          );

          const handleMouseEnter = () => {
            gsap.to(viewProject, {
              opacity: 1,
              scale: 1,
              duration: 0.4,
              ease: "power3.out",
              overwrite: "auto",
            });

            if (image) {
              gsap.to(image, {
                scale: 1.055,
                filter:
                  "brightness(0.9) saturate(1)",
                duration: 0.8,
                ease: "power3.out",
                overwrite: "auto",
              });
            }
          };

          const handleMouseMove = (event) => {
            const bounds =
              imageFrame.getBoundingClientRect();

            const relativeX =
              event.clientX -
              bounds.left -
              bounds.width / 2;

            const relativeY =
              event.clientY -
              bounds.top -
              bounds.height / 2;

            moveX(relativeX * 0.18);

            moveY(relativeY * 0.18);
          };

          const handleMouseLeave = () => {
            gsap.to(viewProject, {
              opacity: 0,
              scale: 0.88,
              x: 0,
              y: 0,
              duration: 0.35,
              ease: "power2.out",
              overwrite: "auto",
            });

            if (image) {
              gsap.to(image, {
                scale: 1.02,
                filter:
                  "brightness(0.72) saturate(0.82)",
                duration: 0.8,
                ease: "power3.out",
                overwrite: "auto",
              });
            }
          };

          imageFrame.addEventListener(
            "mouseenter",
            handleMouseEnter
          );

          imageFrame.addEventListener(
            "mousemove",
            handleMouseMove
          );

          imageFrame.addEventListener(
            "mouseleave",
            handleMouseLeave
          );

          cleanupFunctions.push(() => {
            imageFrame.removeEventListener(
              "mouseenter",
              handleMouseEnter
            );

            imageFrame.removeEventListener(
              "mousemove",
              handleMouseMove
            );

            imageFrame.removeEventListener(
              "mouseleave",
              handleMouseLeave
            );
          });
        });

        return () => {
          cleanupFunctions.forEach((cleanup) => {
            cleanup();
          });
        };
      });
    }, pageRef);

    const refreshTimer = window.setTimeout(() => {
      ScrollTrigger.refresh();
    }, 150);

    return () => {
      window.clearTimeout(refreshTimer);

      projectContext.revert();
    };
  }, [pageRef, activeCategory]);
}

export default useWorkPageAnimations;